import { Dispatch, SetStateAction, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useGGContext } from 'src/components/providers/googleAds';
import { useDisplayGrade } from 'src/hooks/display/useDisplayGrade';
import { IDisplayGrade } from 'src/types/display/DisplayGrade';
import { IAccount } from 'src/types/common/Account';
import { ConditionalClearTimeout, DelayNavigationToTimedOutError, TimeIDRef } from 'src/utils/helpers';
import { ServiceType } from 'src/enums/ServiceType';
import useGATracking from 'src/hooks/google/useGATracking';

const dataLayer = {
  event: 'gaTriggerEvent',
  gaEventCategory: 'Grader',
  gaEventAction: 'Impression',
  gaEventLabel: 'GraderStepReport',
};

interface SetAccountParams {
  grade: IDisplayGrade;
  setter: Dispatch<SetStateAction<IAccount>>;
}

const setAccount = (params: SetAccountParams): void => {
  const accountId = params.grade.overallScore.accountId;
  let displayName = params.grade.overallScore.accountId;
  if (params.grade.overallScore.accountName) {
    displayName = params.grade.overallScore.accountName + ' (' + params.grade.overallScore.accountId + ')';
  }
  params.setter({ accountId, accountName: displayName });
};

interface SetZeroDollarParams {
  grade: IDisplayGrade;
  setter: Dispatch<SetStateAction<boolean>>;
}

const setZeroDollar = (params: SetZeroDollarParams): void => {
  params.setter(params.grade.displayPerformanceMetrics.spend === 0);
};

type ReturnType = {
  isZeroDollarsSpent: boolean;
};

export const useReport = (): ReturnType => {
  const context = useGGContext();
  const { UUID } = useParams();
  const navigate = useNavigate();
  const { displayGrade, displayError } = useDisplayGrade(UUID);
  const [isZeroDollarsSpent, setIsZeroDollarsSpent] = useState<boolean>(false);
  const timeIDRef: TimeIDRef = {
    timeID: undefined,
  };
  const { pushCustomGAEvent } = useGATracking({
    pageViewDefaults: { title: 'GoogleAdsGrader Report' },
    triggerOnMount: true,
  });

  useEffect(() => {
    if (UUID === undefined) navigate('/');
  }, []);

  useEffect(() => {
    if (!displayGrade && !displayError) {
      timeIDRef.timeID = DelayNavigationToTimedOutError(navigate, ServiceType.GOOGLE);
    } else if (displayGrade && !displayError) {
      ConditionalClearTimeout(timeIDRef);
      // Currently we are pushing event only with Search Report
      pushCustomGAEvent({ ...dataLayer, gaEventLabel: 'GraderDisplay' });
      setAccount({ grade: displayGrade, setter: context.setGoogleAdsAccount });
      setZeroDollar({ grade: displayGrade, setter: setIsZeroDollarsSpent });
    } else if (displayError) {
      ConditionalClearTimeout(timeIDRef);
      navigate('/google-grader/error?event=noRoute');
    }

    return () => ConditionalClearTimeout(timeIDRef);
  }, [displayGrade, displayError]);

  return { isZeroDollarsSpent };
};
